import React from 'react';
import {Route, Switch, useRoute} from 'wouter';

import {PageLayout} from '../../components/layout/page-layout';
import {useLastLocationEffect} from '../../hooks/use-last-location-effect';

import {QueriesActivityBar} from './activity-bar';
import {QueriesSidebar} from './queries-sidebar';
import {QueryLayout} from './query-layout/query-layout';
import {usePageLayout} from '../../components/layout/page-layout/use-page-layout';
import {useSidebarTabs} from '../../hooks/use-sidebar-tabs';
import {QueriesTab} from '../../components/layout/page-layout/layout-typings';
import {getLineSeparator} from '../../utils/get-client-os';
import {
    GlobalVersionProps,
    GlobalVersionsLoadedChecker
} from '../gv-management-panel/global-versions-loaded-checker';
import {useGlobalVersion} from '../../providers/version/use-global-version';
import {NewQuery} from './new-query/new-query';

interface Props {
    globalVersionProps: ReturnType<typeof useGlobalVersion>;
}

export function QueryWorkflow(props: Props) {
    const [, queryIdParams] = useRoute('/:queryId?');
    const queryId = queryIdParams ? Number(queryIdParams.queryId) : undefined;

    const tabSettings = useSidebarTabs({
        layout: 'queries',
        activityTabStorageKey: 'queries.lastTab',
        defaultActivityBarTab: QueriesTab.DRAFTS
    });

    const layoutSettings = usePageLayout({
        sidebarSizeStorageKey: 'queries.layoutSize',
        sidebarClosedStorageKey: 'queries.sidebarClosed'
    });

    useLastLocationEffect({
        path: '/queries',
        lsKey: 'queries.lastLocation'
    });

    return (
        <GlobalVersionsLoadedChecker
            errorText={`Knowledge Base global versions is required ${getLineSeparator()} to write and run queries`}
            globalVersionProps={props.globalVersionProps}
        >
            {({currentGlobalVersionData, globalVersions}: GlobalVersionProps) => (
                <PageLayout
                    sidebar={
                        <QueriesSidebar
                            layoutSettings={layoutSettings}
                            tabSettings={tabSettings}
                            queryId={queryId}
                        />
                    }
                    activityBar={
                        <QueriesActivityBar
                            layoutSettings={layoutSettings}
                            tabSettings={tabSettings}
                            currentGlobalVersion={currentGlobalVersionData}
                        />
                    }
                    layoutSettings={layoutSettings}
                >
                    <Switch>
                        <Route
                            path="/new"
                            component={() => (
                                <NewQuery currentGlobalVersion={currentGlobalVersionData} />
                            )}
                        />
                        <Route path="/:queryId">
                            {(params) => (
                                <QueryLayout
                                    queryId={Number(params.queryId)}
                                    globalVersions={globalVersions}
                                    sidebarTabsProps={tabSettings}
                                />
                            )}
                        </Route>
                    </Switch>
                </PageLayout>
            )}
        </GlobalVersionsLoadedChecker>
    );
}
